import {
  ChangeDetectionStrategy,
  Component,
  effect,
  input,
  OnInit,
  output,
  signal,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  AdminMultimediaDetails,
  MultimediaGridLayoutComponent,
  MultimediaGridLayoutItemComponent,
  MultimediaTileComponent,
} from '@web/shared/module/multimedia';
import { Icon, UploadComponent, UploadEvent } from '@web/shared/ui';
import { blank, Guid, Nillable, valueOrDefault } from '@web/utils';
import { combineLatest, Observable, tap } from 'rxjs';
import { WarehouseMultimediaDataSourceService } from '../../services/warehouse-multimedia-data-source.service';
import { FetchWarehouseMultimediaByIdPipe } from '../../pipes/fetch-warehouse-multimedia-by-id.pipe';

@Component({
  selector: 'app-warehouse-file-upload',
  imports: [
    CommonModule,
    MultimediaTileComponent,
    UploadComponent,
    MultimediaGridLayoutComponent,
    MultimediaGridLayoutItemComponent,
    FetchWarehouseMultimediaByIdPipe,
  ],
  templateUrl: './warehouse-file-upload.component.html',
  styleUrl: './warehouse-file-upload.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WarehouseFileUploadComponent implements OnInit {
  /* prettier-ignore */
  private readonly warehouseMultimediaDataSource = inject(WarehouseMultimediaDataSourceService);

  public readonly shouldUpload = input<boolean>(true);
  public readonly maxFileSize = input<number>(2 * 1000 * 1000);
  public readonly allowedMimeTypes = input<string>('');
  public readonly disabled = input(false);
  public readonly selectedFileIds = input<Nillable<Guid[]>>();
  public readonly selectFilesEvent = output<Guid[]>();

  protected readonly uploadQueue = signal<File[]>([]);
  protected readonly uploaded = signal<Guid[]>([]);
  protected readonly Icon = Icon;

  public constructor() {
    this.listenToSelectFilesEvent();
  }

  public ngOnInit(): void {
    this.updateUploadedBySelectedFileIds();
  }

  protected readonly uploadFn = (event: UploadEvent) => this.upload(event);

  protected handleRemoveClickEvent(id: Guid): void {
    this.uploaded.update((ids) =>
      valueOrDefault(ids, []).filter((currentId) => currentId !== id),
    );
  }

  protected shouldShowInfo(item: AdminMultimediaDetails): boolean {
    return !item.isImage;
  }

  private listenToSelectFilesEvent(): void {
    effect(() => this.notifySelectFilesEvent());
  }

  private notifySelectFilesEvent(): void {
    this.selectFilesEvent.emit(this.uploaded());
  }

  private upload(event: UploadEvent): Observable<void> {
    const { files } = event;

    this.addToQueue(files);

    return combineLatest(
      files.map((file) =>
        this.warehouseMultimediaDataSource.upload(file).pipe(
          tap({
            next: (id) => {
              this.removeFromQueue(file);
              this.addToUploaded(id);
            },
            error: () => {
              this.removeFromQueue(file);
            },
          }),
        ),
      ),
    ).pipe(blank());
  }

  private updateUploadedBySelectedFileIds(): void {
    const ids = this.selectedFileIds();

    this.uploaded.set(valueOrDefault(ids, []));
  }

  private addToUploaded(id: Guid): void {
    this.uploaded.update((ids) => [id, ...ids]);
  }

  private addToQueue(filesToAdd: File[]): void {
    this.uploadQueue.update((files) => [...filesToAdd, ...files]);
  }

  private removeFromQueue(fileToRemove: File): void {
    this.uploadQueue.update((result) =>
      result.filter((file) => file !== fileToRemove),
    );
  }
}
